/**
 * useEditable - Create reactive editable wrapper for a node
 *
 * Returns a Proxy wrapper that:
 * - Tracks optimistic property changes
 * - Provides .add()/.remove() for relationships
 * - Re-renders on both optimistic changes and server updates
 * - Reads from current GraphStore state (not stale instance)
 *
 * @example
 * ```tsx
 * function ProductView({ product }: { product: PDMProduct }) {
 *   const editable = useEditable(product);
 *
 *   const handleChange = async (stage: string) => {
 *     editable.development_stage = stage; // Optimistic update
 *     await editable.save(); // Persist to server
 *   };
 *
 *   // Reads from GraphStore + optimistic changes
 *   return <div>{editable.name} - {editable.development_stage}</div>;
 * }
 * ```
 */

import { useMemo } from 'react';
import { useAtomValue, atom } from 'jotai';
import { graphStoreJotaiStore } from '@sourcepilot/framework/graph-store';
import type { EditableWrapper } from '@sourcepilot/framework/editable-wrapper';
import type { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import type { EditableRelationshipManager } from '@sourcepilot/framework/editable-relationship-manager';
import type { RelationshipManager } from '@sourcepilot/framework/relationship-manager';

/**
 * Transform all RelationshipManager properties to EditableRelationshipManager
 * This reflects the runtime behavior of the EditableWrapper Proxy
 */
type TransformRelationships<T> = {
  [K in keyof T]: T[K] extends RelationshipManager<infer Source, infer Target>
    ? EditableRelationshipManager<Target>
    : T[K];
};

/**
 * Utility type for editable nodes with mutation methods
 * Represents what you get from useEditable() - includes transformed relationship properties and editable methods
 *
 * Key transformations:
 * - RelationshipManager<Source, Target> → EditableRelationshipManager<Target> (with .add()/.remove()/.set())
 * - Adds mutation methods: save(), discard(), buildPatch()
 */
export type Editable<T> = TransformRelationships<T> & {
  save(): Promise<void>;
  discard(): void;
  buildPatch(): any;
};

/**
 * useEditable - Returns node wrapped with EditableWrapper providing mutation methods
 *
 * The returned object has:
 * - All properties from T
 * - Mutation methods: save(), discard(), buildPatch()
 * - Relationship properties return EditableRelationshipManager with .add()/.remove()/.set()
 *
 * Note: Due to Proxy pattern, relationship methods require `as any` cast
 */
export function useEditable<T extends NeoNodeBase<any, any>>(
  node: T | null | undefined
): Editable<T> | null {
  // Only recreate when node ID changes, not reference
  const nodeId = (node as any)?.id;

  // Create editable wrapper (stable across re-renders if ID doesn't change)
  // The Proxy returned by makeEditable() acts like T with mutation methods
  // IMPORTANT: Only depend on nodeId, NOT node reference, to preserve pending ops
  // The EditableWrapper reads from GraphStore using nodeId, so it doesn't need
  // the node reference to stay up-to-date with server changes
  const editable = useMemo(() => {
    if (node && nodeId) {
      return node.makeEditable() as unknown as Editable<T>;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  // Create stable fallback atom (only once)
  const fallbackAtom = useMemo(() => atom({}), []);

  // Subscribe to editable wrapper's derived atom for reactivity
  // This triggers re-renders when either:
  // - The GraphStore node changes (server updates via DAG patches)
  // - The optimistic properties change (local edits)
  const derivedAtom = editable
    ? (editable as unknown as EditableWrapper<T>).getVersionAtom()
    : fallbackAtom;
  useAtomValue(derivedAtom, { store: graphStoreJotaiStore });

  return editable;
}
